<template>
    <div class="bg-white shadow-sm p-4 card-content px-0">
        <div class="modal-header d-flex flex-column">
            <h5 class="modal-title font-weight-400">
                Biller Lookup
            </h5>
            <p class="d-flex flex-row align-items-center mb-0">
                <span class="text-muted mb-0">
                     Magetsi integrates with your account management system via a simple API request.
                </span>
            </p>
        </div>
        <div class="p-4">
            <form  method="post">
                <div class="row">
                    <div class="col-12">
                        <div class="form-group mb-3">
                            <label class="mb-0">Endpoint URL</label>
                            <small class="form-text mt-0 text-muted">Enter your full API URL (e.g. https://api.example.com/lookup).</small>
                            <div class="input-group mb-3">
                                <div class="input-group-prepend">
                                    <select v-model="form.method" class="btn form-control dropdown-toggle" style="height: auto !important;">
                                        <option value="GET">GET</option>
                                        <option value="POST">POST</option>
                                    </select>
                                </div>
                                <input type="text" v-model="form.url" placeholder="https://api.example.com/lookup" class="form-control" aria-label="Text input with method selector">
                            </div>
                            <div v-if="form.errors.get('url')" class="text-danger">{{ form.errors.get('url') }}</div>
                            <div v-if="form.errors.get('method')" class="text-danger">{{ form.errors.get('method') }}</div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="form-group mb-3">
                            <label class="mb-0">Headers (JSON format)</label>
                            <small class="form-text mt-0 text-muted">Include required authentication headers in JSON format. Example: { "Authorization": "Bearer token", "X-Application-ID": "id", "X-Application-Secret": "secret" }</small>
                            <textarea
                                rows="5"
                                v-model="form.headers"
                                :class="['form-control', form.errors.get('headers') ? 'is-invalid' : '']"
                                placeholder='{"Authorization": "Bearer your-token","X-Application-ID": "your-app-id","X-Application-Secret": "your-app-secret"}'>
                            </textarea>
                            <div v-if="form.errors.get('headers')" class="text-danger">{{ form.errors.get('headers') }}</div>
                        </div>
                    </div>
                    <div class="col-12" v-if="form.method === 'GET'">
                        <div class="form-group mb-3">
                            <label class="mb-0">Query Template (for GET) - JSON</label>
                            <small class="form-text mt-0 text-muted">Used for GET requests. Replace Account ID dynamically with {account_id}. Example: { "account": "{account_id}" }</small>
                            <textarea rows="3" v-model="form.query_template"
                                      :class="['form-control', form.errors.get('query_template') ? 'is-invalid' : '']"
                                      placeholder='{ "account": "{account_id}" }'></textarea>
                            <div v-if="form.errors.get('query_template')" class="text-danger">{{ form.errors.get('query_template') }}</div>
                        </div>
                    </div>
                    <div class="col-12" v-if="form.method === 'POST'">
                        <div class="form-group mb-3">
                            <label class="mb-0">Body Template (for POST) - JSON</label>
                            <small class="form-text mt-0 text-muted">Used for POST requests. Format your body as JSON with a placeholder. Example: { "account": "{account_id}" }</small>
                            <textarea rows="3" v-model="form.body_template"
                                      :class="['form-control', form.errors.get('body_template') ? 'is-invalid' : '']"
                                      placeholder='{ "account": "{account_id}" }'></textarea>
                            <div v-if="form.errors.get('body_template')" class="text-danger">{{ form.errors.get('body_template') }}</div>
                        </div>


                    </div>
                    <div class="col-12">
                        <hr>
                        <h6 class="modal-title font-weight-400">
                           Test your API
                        </h6>
                        <hr>
                        <div class="form-group mb-3">
                            <label class="mb-0">Example Expected Response</label>
                            <small class="form-text mt-0 text-muted">
                                Below is an example of the expected API response when the lookup is successful.
                            </small>
                            <pre
                                class="p-3 rounded"
                                style="
                                    background-color: #f1f5f6;
                                    color: #727272;
                                    font-size: 14px;
                                    overflow-x: auto;
                                    border: 1px solid #e1e5e6;
                                    line-height: 1.6;
                                    margin-top: 10px;
                                    text-align: left !important;
                                    white-space: pre-line;
                                ">
                                    {
                                      "success": true,
                                      "message": "Account found",
                                      "data": {
                                        "full_name": "John Doe",
                                        "account_type": "Gold",
                                        "status": "Active",
                                        "expires_on": "2025-12-31"
                                      }
                                    }
                            </pre>
                        </div>


                        <div class="form-group mb-3">
                            <label class="mb-0">Test Account ID</label>
                            <input type="text" v-model="form.account_id" placeholder="12345678" class="form-control" aria-label="Text input with method selector">
                        </div>
                        <div v-if="testLoading" class="text-center my-3">
                            <i class="fas fa-spinner fa-4x fa-pulse text-primary"></i>
                        </div>

                        <LookupResult v-if="testResult" :result="testResult" />

                        <div v-else-if="testError" class="alert alert-danger text-center">
                            <p>{{ testError }}</p>
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-row justify-content-between" style="gap: 0.75rem;">
                    <button @click.prevent="testLookup" class="btn btn-success w-50 mt-2" type="button">Test
                    </button>
                    <button
                        @click.prevent="saveConfig"
                        type="button"
                        :disabled="!canSave"
                        :class="['btn w-50 btn-block mt-2', canSave ? 'btn-primary' : 'bg-dark-5 text-white']"
                    >
                        Save
                    </button>

                </div>

            </form>
        </div>
    </div>
</template>

<script>
import Form from '../../../../core/forms/form';
import LookupResult from "./LookupResult.vue";

export default {
    name: "BillerLookup",
    components: {LookupResult},
    props:['corporate'],
    data() {
        const form = new Form({
            method: 'POST',
            url: '',
            headers: '',
            query_template: '',
            body_template: '',
            account_id:''
        });

        form._formatted_headers = () => {
            try {
                return JSON.parse(form.headers || '{}');
            } catch {
                return {};
            }
        };

        form._formatted_query_template = () => {
            try {
                return JSON.parse(form.query_template || '{}');
            } catch {
                return {};
            }
        };

        form._formatted_body_template = () => {
            try {
                return JSON.parse(form.body_template || '{}');
            } catch {
                return {};
            }
        };

        return {
            loading: false,
            testLoading: false,
            testResult: null,
            testError: null,
            form,
        };
    },
    mounted() {
        this.init();
    },
    computed: {
        canSave() {
            return this.testResult &&
                this.testResult.success &&
                (!this.testResult.errors || Object.keys(this.testResult.errors).length === 0);
        }
    },

    methods: {
        f() {
            return f
        },
        autoResize(event) {
            const textarea = event.target;
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
        },
        init() {
            this.loading = true;
            axios.get(`/business/biller-onboarding/lookup-data`)
                .then(response => {
                    const model = response.data.body.biller;
                    this.form.url = model.url;
                    this.form.method = model.method;
                    this.form.headers = JSON.stringify(model.headers || {}, null, 2);
                    this.form.query_template = JSON.stringify(model.query_template || {}, null, 2);
                    this.form.body_template = JSON.stringify(model.body_template || {}, null, 2);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        saveConfig() {
            this.form.submit(`/business/biller-onboarding/lookup-data`)
                .then(() => {
                    this.$toast.success("Config saved successfully!", {
                        timeout: 3000
                    });
                    this.init()
                })
                .catch(error => {
                    console.error(error);
                    this.$toast.error("Failed to save configuration", {timeout: 3000});
                });
        },

        testLookup() {
            this.testLoading = true;
            this.testResult = null;
            this.testError = null;

            this.form.submit(`/business/biller-onboarding/lookup-test`)
                .then((response) => {
                    this.testResult = response.data.body.lookup_result;
                    this.$toast.success("Test successful", { timeout: 3000 });
                })
                .catch(error => {
                    this.testError = error.response?.data?.errors || 'Unexpected error';
                    this.$toast.error("Test failed.", { timeout: 3000 });
                })
                .finally(() => {
                    this.testLoading = false;
                });
        }
    }
}
</script>
